import { useAuth } from '../providers/auth-provider';
import { useProductionLogs } from '@t90/api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ChartTooltip,
  CartesianGrid,
} from '@t90/ui';
import { formatDate, formatNumber } from '@t90/common';

export function ProgressPage() {
  const { trainee } = useAuth();
  const productionFilters = { traineeId: trainee?.id, limit: 30 } as any;
  const { data: productionLogs, isLoading } = useProductionLogs(productionFilters);

  const totalUnits = productionLogs?.reduce((sum, log) => sum + (log.units_completed ?? 0), 0) ?? 0;
  const totalHours = productionLogs?.reduce((sum, log) => sum + (log.hours_worked ?? 0), 0) ?? 0;
  const avgUnitsPerHour = totalHours > 0 ? totalUnits / totalHours : 0;

  // Prepare chart data
  const chartData = productionLogs
    ?.slice()
    .reverse()
    .map((log) => ({
      date: formatDate(log.log_date),
      units: log.units_completed ?? 0,
    })) ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Progress</h1>
        <p className="text-muted-foreground">
          Track your production and program progress
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Units</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalUnits)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalHours.toFixed(1)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Units/Hour</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgUnitsPerHour.toFixed(1)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Production Trend</CardTitle>
          <CardDescription>Your daily unit production over time</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex h-[300px] items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : chartData.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <ChartTooltip />
                  <Area
                    type="monotone"
                    dataKey="units"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary) / 0.2)"
                    name="Units"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-[300px] items-center justify-center text-muted-foreground">
              No production data available yet.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Production History</CardTitle>
          <CardDescription>Your recent production logs</CardDescription>
        </CardHeader>
        <CardContent>
          {productionLogs?.length ? (
            <div className="space-y-3">
              {productionLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div>
                    <p className="font-medium">{formatDate(log.log_date)}</p>
                    <p className="text-sm text-muted-foreground">
                      {log.project_id ? `Project ${log.project_id.substring(0, 8)}...` : 'Unknown Project'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatNumber(log.units_completed ?? 0)} units</p>
                    <p className="text-xs text-muted-foreground">
                      {log.hours_worked?.toFixed(1)} hrs
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No production logs recorded yet.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
